<template>
  <q-card-section class="column col items-center gap-lg q-pt-xl q-px-xl q-pb-md">
    <div class="onboarding-icon-badge row items-center justify-center">
      <lucide-icon
        :name="copy.icon"
        size="40px"
        :stroke-width="2"
        aria-hidden="true"
      />
    </div>

    <div class="column items-center gap-md text-center">
      <h1 class="text-28 text-font-semibold">{{ copy.headline }}</h1>
      <div
        v-if="copy.subline?.length"
        class="onboarding-subline column items-center gap-xs text-16 text-primary"
      >
        <p
          v-for="(line, index) in copy.subline"
          :key="index"
          class="q-ma-none"
        >
          {{ line }}
        </p>
      </div>
    </div>

    <q-form
      class="column col-grow justify-between gap-lg full-width"
      @submit.prevent="onSubmit"
    >
      <div class="column gap-md">
        <div class="column gap-xs no-wrap">
          <label
            for="onboarding-account-name"
            class="text-14 text-font-semibold"
          >
            {{ copy.accountNameLabel }}
          </label>
          <q-input
            v-model="accountName"
            for="onboarding-account-name"
            v-bind="FIELD_CHROME.form"
            autofocus
            dense
            :maxlength="MAX_LENGTH.MEDIUM_TEXT"
          />
        </div>

        <div class="column gap-xs no-wrap">
          <label
            for="onboarding-company-name"
            class="text-14 text-font-semibold"
          >
            {{ copy.companyNameLabel }}
          </label>
          <q-input
            v-model="companyName"
            for="onboarding-company-name"
            v-bind="FIELD_CHROME.form"
            dense
            :maxlength="MAX_LENGTH.MEDIUM_TEXT"
            :placeholder="copy.companyNamePlaceholder"
          />
        </div>

        <div class="column gap-xs no-wrap">
          <label
            for="onboarding-country"
            class="text-14 text-font-semibold"
          >
            {{ copy.countryLabel }}
          </label>
          <country-select
            v-model="country"
            for="onboarding-country"
            :placeholder="copy.countryPlaceholder"
          />
        </div>

        <onboarding-name-fields
          v-model:first-name="nameFields.firstName"
          v-model:last-name="nameFields.lastName"
        />
      </div>

      <div class="column items-center gap-xs">
        <q-btn
          flat
          no-caps
          color="secondary"
          class="onboarding-action-btn sm-radius text-14"
          @click="onUseDifferentAccount"
        >
          {{ ONBOARDING_COPY.secondaryActionLabel }}
        </q-btn>
        <q-btn
          type="submit"
          no-caps
          unelevated
          color="brand"
          class="onboarding-action-btn sm-radius text-14 text-font-semibold"
          :disable="!canSubmit"
        >
          {{ copy.ctaLabel }}
          <lucide-icon
            :name="copy.ctaIcon"
            size="18px"
            color="white"
            class="q-ml-xs"
            aria-hidden="true"
          />
        </q-btn>
      </div>
    </q-form>
  </q-card-section>
</template>

<script setup>
  import { computed, reactive, ref } from 'vue'

  import { useAccountStore } from 'src/stores/account'
  import { useOnboardingStore } from 'src/stores/account/onboarding'
  import { useAuthStore } from 'src/stores/auth'

  import {
    FIELD_CHROME,
    MAX_LENGTH,
    ONBOARDING_COPY,
    ONBOARDING_FLOW_TYPE,
  } from 'src/services/consts/app.const'
  import { hasLetterOrNumber } from 'src/services/helpers/util.helper'

  import CountrySelect from 'src/components/common/CountrySelect.vue'
  import OnboardingNameFields from 'src/components/auth/OnboardingNameFields.vue'

  const accountStore = useAccountStore()
  const onboardingStore = useOnboardingStore()
  const authStore = useAuthStore()

  const copy = ONBOARDING_COPY.founderWelcome

  const accountName = ref('')
  const companyName = ref('')
  const country = ref(null)
  const nameFields = reactive({
    firstName: accountStore.accountData?.firstName ?? '',
    lastName: accountStore.accountData?.lastName ?? '',
  })

  const canSubmit = computed(
    () =>
      hasLetterOrNumber(accountName.value) &&
      (!companyName.value || hasLetterOrNumber(companyName.value)) &&
      hasLetterOrNumber(nameFields.firstName) &&
      hasLetterOrNumber(nameFields.lastName)
  )

  // The account already exists server-side, so this only abandons setup — the
  // next sign-in lands back on this panel.
  function onUseDifferentAccount() {
    authStore.logout()
  }

  function onSubmit() {
    if (!canSubmit.value) return
    onboardingStore.saveWelcomeAndAdvanceToTerms({
      flowType: ONBOARDING_FLOW_TYPE.FOUNDER,
      accountName: accountName.value.trim(),
      companyName: companyName.value.trim() || '',
      country: country.value,
      firstName: nameFields.firstName.trim(),
      lastName: nameFields.lastName.trim(),
    })
  }
</script>
